/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from '@emotion/styled'

import DataTable from 'components/atoms/DataTable'
import { TableWrapper } from 'components/atoms/TableWrapper'
import Toast from 'components/atoms/Toast'
import { confirm } from 'components/molecules/Confirmation'
import CatalogItemImage from 'components/atoms/CatalogItemImage'
import { useDeleteCatalogItemMutation, useGetCatalogItemsQuery } from 'store/services/catalog'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from 'components/lab/user/permissions'
import DashboardContent from '../atoms/DashboardContent'
import ButtonNew from '../../atoms/ButtonNew'
import Input from '../../atoms/Input'
import routes from '../constants/routes'

const StyledTableWrapper = styled(TableWrapper)`
  max-height: calc(100vh - 230px);
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.6rem;
  }
`

const PAGE_SIZE = 10

const CatalogDirectory = () => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(0)
  const canEdit = useLabPermission(UserPermission.EDIT_CATALOG)
  const { data: items, isLoading } = useGetCatalogItemsQuery({ by: 'lab' })
  const [deleteCatalogItem] = useDeleteCatalogItemMutation()

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) {
      return items ?? []
    }
    return (items ?? []).filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        (item.description ?? '').toLowerCase().includes(query),
    )
  }, [items, search])

  const openItem = (id: string) => {
    navigate(`${routes.index}${routes.catalog.root}${routes.upsert}${id}`)
  }

  const onDelete = (id: string, name: string) => {
    confirm(`catalog item "${name}"`)
      .then(async () => {
        try {
          await deleteCatalogItem({ id }).unwrap()
          Toast({ type: 'success', label: 'Catalog item removed!' })
        } catch (err: any) {
          Toast({ type: 'error', label: err?.data?.message || 'Could not remove catalog item.' })
        }
      })
      .catch(() => {})
  }

  const header = [
    {
      accessor: 'picture',
      Header: 'Picture',
      Cell: ({ row }) => (
        <CatalogItemImage
          id={row.original.id}
          by="lab"
          hasPicture={Boolean(row.original.pictureKey)}
          width={48}
          height={48}
        />
      ),
    },
    { accessor: 'name', Header: 'Name' },
    { accessor: 'sku', Header: 'SKU' },
    {
      accessor: 'description',
      Header: 'Description',
      Cell: ({ row }) => row.original.description || '—',
    },
    {
      accessor: 'purchaseUom',
      Header: 'Purchase UOM',
      Cell: ({ row }) => row.original.purchaseUom || '—',
    },
    {
      accessor: 'quantityPerPurchaseUom',
      Header: 'Qty / UOM',
      Cell: ({ row }) =>
        row.original.quantityPerPurchaseUom === null ||
        typeof row.original.quantityPerPurchaseUom === 'undefined'
          ? '—'
          : row.original.quantityPerPurchaseUom,
    },
    ...(canEdit
      ? [
          {
            accessor: 'actions',
            Header: '',
            Cell: ({ row }) => (
              <i
                className="fa fa-times-circle"
                aria-hidden="true"
                css={{ cursor: 'pointer', color: '#dc3545' }}
                onClick={(event) => {
                  event.stopPropagation()
                  onDelete(row.original.id, row.original.name)
                }}
              />
            ),
          },
        ]
      : []),
  ]

  return (
    <DashboardContent
      title="Catalog Management"
      icon="fas fa-boxes"
      content={
        <div className="card card-primary">
          <form>
            <div className="card-header row">
              <div className="col-10 input-group mb-3">
                <Input
                  autoFocus={true}
                  value={search}
                  label={false}
                  placeholder="Search..."
                  name="search"
                  parentDiv={null}
                  handleChange={(event) => setSearch(event.target.value)}
                />
                <div className="input-group-append">
                  <span className="input-group-text">
                    <i className="fas fa-search" />
                  </span>
                </div>
              </div>

              {canEdit && (
                <div className="col-2 text-right">
                  <ButtonNew to={`${routes.index}${routes.catalog.root}${routes.upsert}`} />
                </div>
              )}
            </div>
          </form>
          <div className="card-body align-items-center">
            <StyledTableWrapper className="card-body row">
              <DataTable
                tableKey={`catalog_items`}
                data={{
                  items: filteredItems,
                  totalItems: filteredItems.length,
                  currentPage: 1,
                }}
                status={isLoading}
                tableColumns={header}
                pageSize={PAGE_SIZE}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                setSortOptions={() => {}}
                entityName="catalog items"
                onRowClick={(row) => openItem(row.original.id)}
              />
            </StyledTableWrapper>
          </div>
        </div>
      }
      breadcrumb={[{ label: 'Catalog Management' }]}
    />
  )
}

export default CatalogDirectory
